import React, { useState } from "react";
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube, Linkedin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollPosition } from "../hooks/useScrollPosition";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { MenuIcon } from "./icons/MenuIcon";
import { CloseIcon } from "./icons/CloseIcon";
import ApplyNowModal from "../components/ApplyNowModel.js";
import { motion, AnimatePresence } from "framer-motion";

// Menu items configuration
const MENU_ITEMS = [
  { name: 'Home', href: '/', isRoute: true },
  { name: 'About', href: '/about', isRoute: true },
  { name: 'Properties', href: '/properties', isRoute: true },
  { name: 'Contact', href: '/contact', isRoute: true },
  { name: 'Requirements', href: '/requirements', isRoute: true },
];

function Footer() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Use custom hooks for cleaner side-effect management
  const scrolled = useScrollPosition(20);
  useBodyScrollLock(isMobileMenuOpen || isModalOpen);

  // Define handlers for better readability
  const handleCloseMobileMenu = () => setIsMobileMenuOpen(false);

  const handleOpenModal = () => {
    setIsMobileMenuOpen(false); // Ensure mobile menu is closed
    setIsModalOpen(true);
  };

  const NavLink = ({ item, onClick }) =>
    item.isRoute ? (
      <Link
        to={item.href}
        className="text-sm xl:text-base text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200 whitespace-nowrap"
        onClick={onClick}
      >
        {item.name}
      </Link>
    ) : (
      <a
        href={item.href}
        className="text-sm xl:text-base text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200 whitespace-nowrap"
        onClick={onClick}
      >
        {item.name}
      </a>
    );

  return (
    <>
      {/* Hero Section */}
      <div className="relative bg-cover bg-center h-96 bg-gray-600">
        <img
          src="/assets/heroFooter.png"
          alt="Modern home interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 flex items-center justify-center h-full text-center text-white p-4">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              READY TO RENT YOUR NEXT RENTAL?
            </h2>
            <p className="text-lg md:text-xl mb-6">
              Apply today and let Raulfex match you with the perfect property.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleOpenModal}
                className="px-6 xl:px-8 py-3 text-sm xl:text-base rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap"
              >
                Apply Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Section */}
      <footer className="bg-black text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Contact Us Section */}
            <div>
              <h3 className="text-xl font-semibold mb-6">Contact Us</h3>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 text-orange-500 mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <p>20 Mowbray Avenue</p>
                    <p>Benoni</p>
                    <p>1500</p>
                  </div>
                </div>
                <a
                  href="tel:0631315814"
                  className="flex items-center hover:text-white transition-colors duration-200"
                >
                  <Phone className="w-5 h-5 text-orange-500 mr-4 flex-shrink-0" />
                  <p>063 131 5814 | (016) 806 7097</p>
                </a>
                <a
                  href="mailto:raulfex.properties@gmail.com"
                  className="flex items-center hover:text-white transition-colors duration-200"
                >
                  <Mail className="w-5 h-5 text-orange-500 mr-4 flex-shrink-0" />
                  <p>raulfex.properties@gmail.com</p>
                </a>
              </div>
            </div>

            {/* Quick Links Section */}
            <div>
              <h3 className="text-xl font-semibold mb-6">Quick Links</h3>
              <ul className="space-y-3 text-gray-300">
                <li>
                  <Link to="/" className="hover:text-white hover:underline transition-all duration-200">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:text-white hover:underline transition-all duration-200">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white hover:underline transition-all duration-200">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/requirements" className="hover:text-white hover:underline transition-all duration-200">
                    Requirements
                  </Link>
                </li>
              </ul>
            </div>

            {/* Join Our Newsletter Section */}
            <div>
              <h3 className="text-xl font-semibold mb-6">Join Our Newsletter</h3>
              <p className="text-gray-300 mb-4">Sign up for updates on our latest news.</p>
              <div className="flex items-center bg-white rounded-full p-1 max-w-sm mb-4">
                <input
                  type="email"
                  placeholder="Enter your e-mail"
                  className="flex-grow bg-transparent px-4 py-2 text-black placeholder-gray-500 focus:outline-none"
                />
                <button
                  className="bg-orange-500 hover:bg-orange-600 text-white w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 group relative z-10 cursor-pointer"
                  aria-label="Subscribe to newsletter"
                >
                  <ArrowRight
                    size={20}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">
                By clicking subscribe, you agree to the{' '}
                <Link to="/terms" className="text-white hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-white hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>

          {/* Sub-Footer with Copyright and Socials */}
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center text-sm text-gray-400">
            <p className="mb-4 sm:mb-0">Copyright © 2025 Raulfex Pty Ltd. All Rights Reserved.</p>
            <div className="flex items-center space-x-4">
              <span>Follow us :</span>
              <a
                href="#"
                className="text-orange-500 hover:text-white transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="#"
                className="text-orange-500 hover:text-white transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="#"
                className="text-orange-500 hover:text-white transition-colors duration-200"
                aria-label="YouTube"
              >
                <Youtube size={20} />
              </a>
              <a
                href="#"
                className="text-orange-500 hover:text-white transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={handleCloseMobileMenu}
        />
      )}

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-80 bg-white shadow-2xl z-40 lg:hidden transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full pt-20 px-6 pb-8">
          <nav className="flex flex-col gap-1 mb-8">
            {MENU_ITEMS.map((item) => (
              <NavLink key={item.name} item={item} onClick={handleCloseMobileMenu} />
            ))}
          </nav>
          <div className="flex flex-col gap-3 mt-auto">
            <Link to="/contact" className="w-full">
              <button
                className="w-full px-6 py-3 text-base rounded-full border-2 border-gray-900 text-gray-900 font-semibold hover:bg-gray-900 hover:text-white transition-all duration-200 active:scale-95"
                onClick={handleCloseMobileMenu}
              >
                Contact us
              </button>
            </Link>
            <button
              className="w-full px-6 py-3 text-base rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-all duration-200 active:scale-95"
              onClick={handleOpenModal}
            >
              Apply Now
            </button>
          </div>
        </div>
      </div>

      {/* Apply Now Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ApplyNowModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Footer;